import { useState } from "react";
import { UserPlus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { LEAD_SOURCES } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VehicleFields } from "@/components/VehicleFields";

const BLANK = { name: "", phone: "", make: "", model: "", colour: "", plate: "", lead_source: "" };
const NEW_VEHICLE = "__new__";
const vehLabel = (v) =>
  [v.make, v.model, v.colour, v.plate].filter(Boolean).join(" · ") || "Vehicle";

// Creates a Lead-stage job — either for a freshly typed customer, or for an existing
// customer whose known vehicles auto-populate a dropdown so the right car is picked.
export const CreateLeadDialog = ({ onCreated, trigger }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const [customer, setCustomer] = useState(null);     // selected existing customer
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [vehicles, setVehicles] = useState([]);       // known vehicles for the customer
  const [vehIdx, setVehIdx] = useState(NEW_VEHICLE);

  const reset = () => {
    setF(BLANK); setCustomer(null); setSearch(""); setResults([]);
    setVehicles([]); setVehIdx(NEW_VEHICLE);
  };

  const runSearch = async (q) => {
    setSearch(q);
    if (q.trim().length < 2) { setResults([]); return; }
    try {
      const { data } = await api.get(`/customers?search=${encodeURIComponent(q.trim())}`);
      setResults((data || []).slice(0, 8));
    } catch { setResults([]); }
  };

  const pickCustomer = async (c) => {
    setCustomer(c);
    setResults([]);
    setSearch("");
    const v = c.vehicle || {};
    setF((s) => ({
      ...s, name: c.name || "", phone: c.phone || "", lead_source: c.lead_source || "",
      make: v.make || "", model: v.model || "", colour: v.colour || "", plate: v.plate || "",
    }));
    try {
      const { data } = await api.get(`/customers/${c.id}/vehicles`);
      const list = data || [];
      setVehicles(list);
      if (list.length) applyVehicle("0", list);
      else setVehIdx(NEW_VEHICLE);
    } catch { setVehicles([]); setVehIdx(NEW_VEHICLE); }
  };

  const applyVehicle = (idx, list = vehicles) => {
    setVehIdx(idx);
    if (idx === NEW_VEHICLE) {
      setF((s) => ({ ...s, make: "", model: "", colour: "", plate: "" }));
      return;
    }
    const v = list[Number(idx)] || {};
    setF((s) => ({ ...s, make: v.make || "", model: v.model || "", colour: v.colour || "", plate: v.plate || "" }));
  };

  const clearCustomer = () => reset();

  const submit = async () => {
    const vehicle = { make: f.make, model: f.model, colour: f.colour, plate: f.plate };
    setBusy(true);
    try {
      let customerId = customer?.id;
      if (!customerId) {
        if (!f.name.trim()) { toast.error("Enter the customer name"); setBusy(false); return; }
        if (!f.phone.trim()) { toast.error("Enter the contact number"); setBusy(false); return; }
        const { data: cust } = await api.post("/customers", {
          name: f.name.trim(), phone: f.phone.trim(), lead_source: f.lead_source || "", vehicle,
        });
        customerId = cust.id;
      }
      await api.post("/jobs", { customer_id: customerId, status: "Lead", services: [], price: 0, vehicle });
      toast.success("Lead created");
      reset();
      setOpen(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" data-testid="create-lead-btn">
            <UserPlus className="mr-2 h-4 w-4" /> Create Lead
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="create-lead-dialog">
        <DialogHeader>
          <DialogTitle>Create a new lead</DialogTitle>
        </DialogHeader>

        {/* Existing-customer search */}
        {!customer ? (
          <div className="relative">
            <Label className="text-xs">Find an existing customer (optional)</Label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                className="pl-8"
                data-testid="lead-customer-search"
                placeholder="Search by name or phone"
                value={search}
                onChange={(e) => runSearch(e.target.value)}
              />
            </div>
            {results.length > 0 && (
              <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
                   data-testid="lead-customer-results">
                {results.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`lead-customer-option-${c.id}`}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                    onClick={() => pickCustomer(c)}
                  >
                    <span className="font-medium">{c.name}</span>
                    <span className="text-slate-500"> · {c.phone}</span>
                    {c.vehicle?.make && (
                      <span className="text-slate-400"> · {c.vehicle.make} {c.vehicle.model}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between rounded-md border border-brand-line bg-slate-50 px-3 py-2"
               data-testid="lead-selected-customer">
            <div className="text-sm">
              <span className="font-medium">{customer.name}</span>
              <span className="text-slate-500"> · {customer.phone}</span>
            </div>
            <Button variant="ghost" size="sm" data-testid="lead-clear-customer" onClick={clearCustomer}>
              <X className="mr-1 h-3.5 w-3.5" /> Change
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {!customer && (
            <>
              <div>
                <Label className="text-xs">Customer name *</Label>
                <Input className="mt-1" data-testid="lead-name" value={f.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Contact number *</Label>
                <Input className="mt-1" data-testid="lead-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
            </>
          )}

          {/* Vehicle dropdown for an existing customer */}
          {customer && (
            <div className="sm:col-span-2">
              <Label className="text-xs">Vehicle for this lead</Label>
              <Select value={vehIdx} onValueChange={(v) => applyVehicle(v)}>
                <SelectTrigger className="mt-1" data-testid="lead-vehicle-select">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((v, i) => (
                    <SelectItem key={i} value={String(i)}>{vehLabel(v)}</SelectItem>
                  ))}
                  <SelectItem value={NEW_VEHICLE}>+ Add a different vehicle</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="sm:col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <VehicleFields
              testidPrefix="lead"
              value={{ make: f.make, model: f.model, colour: f.colour, plate: f.plate }}
              onChange={(veh) => setF((s) => ({ ...s, ...veh }))}
            />
          </div>
          {!customer && (
            <div>
              <Label className="text-xs">Lead source</Label>
              <Select value={f.lead_source} onValueChange={(v) => set("lead_source", v)}>
                <SelectTrigger className="mt-1" data-testid="lead-source"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
          <Button data-testid="submit-lead-btn" disabled={busy} onClick={submit}>Create lead</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
